import { formatDuration, riyadhBusinessDate } from '@room-verify/shared';
import { api, type InspectionListItem } from '../api';
import { ar, arDate, arDateShort, arWeekday } from '../ar';
import {
  IconAlert,
  IconCalendar,
  IconCheckCircle,
  IconClock,
  IconPencil,
  IconRefresh,
  IconUser,
} from '../components/icons';
import {
  InspectionList,
  InspectionListSkeleton,
} from '../components/inspections';
import {
  DayPanel,
  ExceptionMetric,
  ExceptionRow,
  PrimaryMetric,
  SecondaryMetric,
} from '../components/metrics';
import { Button, EmptyState, Notice } from '../components/primitives';
import { PageSection, TopBar } from '../components/shell';
import { useAsync } from '../hooks';

/**
 * The first viewport answers, in this order: how many rooms, what kind, and is anything
 * unusual. That ranking is expressed as size — one dominant figure, a supporting pair,
 * then a quiet row of exceptions — rather than four cards shouting equally.
 */
export function OverviewScreen({
  token,
  date,
  onDateChange,
  onOpenReport,
  onEdit,
  onNavigate,
  onOpenDrawer,
  onAuthExpired,
  reloadKey,
}: {
  token: string;
  date: string;
  onDateChange: (date: string) => void;
  onOpenReport: (id: string) => void;
  onEdit: (item: InspectionListItem) => void;
  onNavigate: (hash: string) => void;
  onOpenDrawer: () => void;
  onAuthExpired: () => void;
  reloadKey: number;
}) {
  const today = riyadhBusinessDate();

  const dashboard = useAsync(
    () => api.dashboard(token, date),
    [token, date, reloadKey],
    onAuthExpired,
  );

  const activity = useAsync(
    () => api.inspections(token, { from: date, to: date, limit: 200 }),
    [token, date, reloadKey],
    onAuthExpired,
  );

  const stats = dashboard.data?.stats;
  const items = activity.data?.results ?? [];
  const total = stats?.total ?? 0;

  const iso = `${date}T09:00:00.000Z`;
  // the long month name is ellipsised on a phone; the short form fits
  const compact = typeof window !== 'undefined' && window.innerWidth < 480;

  return (
    <>
      <TopBar
        title={ar.navOverview}
        subtitle={ar.overviewSubtitle(arWeekday(iso), compact ? arDateShort(iso) : arDate(iso))}
        onOpenDrawer={onOpenDrawer}
        actions={
          <div className="topbar-controls">
            <label className="filter-date">
              <IconCalendar size={15} />
              <input
                className="filter-date-input num"
                type="date"
                value={date}
                max={today}
                onChange={(event) => onDateChange(event.target.value || today)}
                dir="ltr"
                aria-label={ar.date}
              />
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                dashboard.reload();
                activity.reload();
              }}
              aria-label={ar.refresh}
              icon={<IconRefresh size={15} />}
            />
          </div>
        }
      />

      <div className="page page-dashboard">
        {dashboard.error && (
          <Notice tone="critical" role="alert">
            {dashboard.error}
          </Notice>
        )}

        {/* One surface: a dominant total, then the type split beside it. */}
        <DayPanel>
          <PrimaryMetric
            label={ar.totalInspections}
            value={total}
            loading={dashboard.loading && !stats}
            caption={
              total > 0 ? (
                <span className="metric-caption-text">{ar.inspectionsCaption}</span>
              ) : null
            }
          >
            {dashboard.data && dashboard.data.bySupervisor.length > 0 && (
              <div className="supervisor-strip">
                <span className="eyebrow">{ar.bySupervisor}</span>
                <ul className="supervisor-list">
                  {dashboard.data.bySupervisor.map((row) => (
                    <li key={row.supervisorId} className="supervisor-pill">
                      <IconUser size={13} />
                      <span className="supervisor-pill-name">{row.name}</span>
                      <span className="supervisor-pill-count num">{row.total}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </PrimaryMetric>

          <SecondaryMetric
            label={ar.checkoutRooms}
            value={stats?.checkout ?? 0}
            share={total > 0 ? (stats?.checkout ?? 0) / total : 0}
            loading={dashboard.loading && !stats}
          />
          <SecondaryMetric
            label={ar.cleanRooms}
            value={stats?.clean ?? 0}
            share={total > 0 ? (stats?.clean ?? 0) / total : 0}
            tone="accent"
            loading={dashboard.loading && !stats}
          />
        </DayPanel>

        {/* Exceptions stay neutral when there is nothing to flag. */}
        <ExceptionRow>
          <ExceptionMetric
            label={ar.withMissingItems}
            value={stats?.withMissingItems ?? 0}
            tone={(stats?.withMissingItems ?? 0) > 0 ? 'warning' : 'neutral'}
            icon={<IconAlert size={15} />}
            loading={dashboard.loading && !stats}
            onClick={
              (stats?.withMissingItems ?? 0) > 0
                ? () => onNavigate('#/reports?missingItems=yes')
                : undefined
            }
          />
          <ExceptionMetric
            label={ar.editedReports}
            value={stats?.edited ?? 0}
            tone={(stats?.edited ?? 0) > 0 ? 'accent' : 'neutral'}
            icon={<IconPencil size={15} />}
            loading={dashboard.loading && !stats}
            onClick={(stats?.edited ?? 0) > 0 ? () => onNavigate('#/audit') : undefined}
          />
          <ExceptionMetric
            label={ar.averageDuration}
            value={formatDuration(stats?.avgDurationSeconds ?? 0)}
            icon={<IconClock size={15} />}
            loading={dashboard.loading && !stats}
          />
        </ExceptionRow>

        <PageSection
          id="activity"
          title={ar.todaysActivity}
          count={items.length > 0 ? items.length : undefined}
          action={
            items.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => onNavigate('#/inspections')}>
                {ar.viewAll}
              </Button>
            ) : undefined
          }
        >
          {activity.error ? (
            <Notice tone="critical" role="alert">
              {activity.error}
            </Notice>
          ) : activity.loading && items.length === 0 ? (
            <InspectionListSkeleton />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<IconCheckCircle size={26} />}
              title={ar.noReportsTitle}
              text={ar.noReportsText}
            />
          ) : (
            <InspectionList items={items.slice(0, 8)} onOpen={onOpenReport} onEdit={onEdit} />
          )}
        </PageSection>
      </div>
    </>
  );
}
